import { useEditable } from 'use-editable';

import './App.css';
import React, { useState, useRef } from 'react';
import { lex } from './lex.js';
import { parse } from './parse.js';
import { interpret, printOutput } from './interpreter.js';

const global = window
global.my_log = ""
global.postALog = (whatever, indent = " ") => {
  global.my_log += `${indent}${whatever}\n`
}

const FC_Error = Error

function App() {
  const FreeCellButton = ({children, onClick, moreClasses}) => (
    <button className={
      `my-button ${moreClasses}`
    } style={{
      appearance: 'none',
      border: 'var(--border-width) solid var(--border)',
      height: '40px',
      minWidth: '120px',
      fontSize: '16px',
      color: 'var(--foreground)',
      padding: '0 1em',
    }} onClick={onClick}>{children}</button>
  )

  const ExampleButton = ({ sourceContent, label }) => (
    <FreeCellButton moreClasses={sourceContent === content ? 'active' : ''} onClick={() => {
      setContent(sourceContent)
    }}>{label}</FreeCellButton>
  )

  const _NotifyBadge = ({ label }) => (
    <button className="my-button" style={{
      appearance: 'none',
      border: 'var(--border-width) solid var(--border)',
      height: '40px',
      minWidth: '120px',
      fontSize: '16px',
      color: 'var(--foreground)',
      padding: '0 1em',
      cursor: 'default',
      backgroundColor: 'var(--alert)',
    }}>{label}</button>
  )

  const forceString = (s) => (
    (typeof s === 'string' || s instanceof String) ? s : JSON.stringify(s, null, 2)
  )

  const blank_example = ``;
  const storage_example = `
# values can be stored declaratively under names using the arrow operators: leftwards or rightwards.

# redeclaring the value stored under a name is an error!

# storing a value under two names (using both arrow directions on the same expression) is an error!

x <- 1
2 -> y

# values can be retrieved by using their name in an expression.
# circular references will cause an error!

x + y -> z
w <- x * y
\n`.trimStart();
  const invocation_example = `
# function invocations use parentheses with comma-separated arguments inside

Math.add(1, 2) -> addition_result

# after nesting a few function calls, the flow of data can become hard to read

String.append(String.append(String.upcase(String.twice("hello")), " "), "world")
-> greeting_1

# instead, pipelines can be used!

"hello"
|> String.twice()
|> String.upcase()
|> String.append(" ")
|> String.append("world")
-> greeting_2

# like arrow storage, pipelines work in either direction (but not both!)

laughter_sound <- "ha"
laughing <- String.twice() <| laughter_sound
so_much_laughing <- String.twice() <| String.twice() <| laughter_sound

# oh I guess they can both be used, right taking precedence. 
# don't rely on this. it should be a parsing error.
x <- String.append("left") <| "" |> String.append("right")
\n`.trimStart()
  const math_example = `
# FREE CELL has 1 number type: rationals. 
# internally, all numbers are represented with a numerator and denominator
# this alleviates any unexpected results that happen normally with ieee754 floats

three_tenths <- 0.1 + 0.2
zero <- three_tenths - 0.3

# these operators are built-in:
sum <- 1.5 + 2.3
difference <- 8.4 - 4.6
product <- 3.1 * 5.2
quotient <- 9.7 / 7.8

# the exponent operator requires the exponent to have a denominator of 1

raised <- 2 ^ 4

# the modulus operator requires both operands to have a denominator of 1
modulo_0 <- 3 % 3
modulo_1 <- 4 % 3
modulo_2 <- 5 % 3

# roots and other real (but irrational) numbers aren't properly supported yet.
# the single number type should eventually gain irrational.
# it may one day gain additional dimensions (i, j, k, etc) support.

# for now, any roots will need to be approximated as rationals

sq_2 <- 665857/470832
sq_2 |> Math.to_float() -> estimated_sq_2

sq_2_ratio <- sq_2
format_as_ratio sq_2_ratio

\n`.trimStart()
  const list_example = `
# List literals don't exist yet,
#   so hand-written lists are built up using functions.

List.empty()
|> List.append(1)
|> List.append(2)
|> List.append(3)
-> numbers
\n`.trimStart()
  const full_example = `
# values don't need to be setup in order, 
#   each of these lines are declarative definitions

greeting1 <- String.append(String.append("hello", space), "world")

# pipelines are neat

"hello"
|> String.append(space)
|> String.append("world")
-> greeting2

# order of operations doesn't exist.
# every math op is the same precedence, so you must disambiguate manually

a <- b + 2
b <- (5 - c) * 5

# leftwards pipes and storage

c <- Math.add(1) <| Math.add(1) <| Math.add(1) <| 1

# setup a helper to be used in that first long pipeline
space <- " "
# hide the helper's name to prevent it from appearing in the output
hide space
\n`.trimStart()
  const test_all = `
(((((1 + 2) * 3) - 4)) ^ 3) / 100
-> a
b <- 0.2 + a
hide c
c <- 14/6
b + c -> d
0.1 + 0.2 -> e
f <- 3 % 6
format_as_float a

"red"
|> String.append(",")
|> String.append("blue")
-> g
h <- String.append("left", String.append("-middle-", "right"))
i <- String.append("world") <| "hello"
\n`.trimStart()

const function_example = `
# defining a new pure function is easy! just give it a name and a body. 
# arguments aren't declared, simply used in the body via $n values.
function greet {
  "hello "
  |> String.append($1)
  |> String.append("!")
}

greeting <- greet("stranger")

# values can be stored to a name within a function body. 
# those are scoped to the body and don't effect the global scope.
# shadowing (using the same name as a global) is disallowed to minimize mistakes.

# function bodies disallow output formatting statements (hide, etc).
# function bodies expect any number of storage statements and exactly 1 bare expression.
# the order of statements in a function body doesn't matter. 
# the 1 bare expression is the return value.

# x: -5/1
x <- fancy_fun(4, 5)

function fancy_fun {
  (a + b) - c

  a <- ($1 / 2) + c
  b <- 3 * (4 - $2)
  c <- b + 1
}

\n`.trimStart()

  const welcome_notes = `
# welcome to FREECELL, a declarative language system!
#
# use the buttons across the top to browse some code examples
#
# phase 1 features:
#   [x] numbers and strings
#   [x] numbers are rationals instead of floats
#   [x] numbers self-reduce when possible
#   [x] value storage
#   [x] function calls
#   [x] user-defined pure functions
#   [x] scoped-variable support in pure functions
#   [x] pipeline operator
#   [x] output all declared values
#   [x] "hide" keyword
#   [x] format_as_ratio keyword
#   [x] math operators: + - * / % ^
#   [ ] useful stdlib string functions
#   [ ] useful stdlib number functions
#   [x] url fragment shortcuts to examples
# 
# phase 2 features:
#   [ ] atoms / symbols
#   [ ] useful stdlib atom/symbol functions
#   [ ] complex types: lists and dictionaries
#   [ ] useful stdlib list functions
#   [ ] useful stdlib dictionary functions
#   [ ] improved error messages
#   [ ] contents as url fragment
#   [x] solo expression output
#
# phase 3 features:
#   [ ] user-defined unnamed imperative definitions
#   [ ] user-defined named imperative definitions
#   [ ] useful stdlib functions around imperatives
#   [ ] irrational number support
#   [ ] artbitrary number units
#   [ ] fractional exponents and modulo
#
\n`.trimStart()


    let default_message = welcome_notes

    switch(window.location.hash) {
      case '#blank': default_message = blank_example; break;
      case '#storage': default_message = storage_example; break;
      case '#invocation': default_message = invocation_example; break;
      case '#function': default_message = function_example; break;
      case '#math': default_message = math_example; break;
      case '#full': default_message = full_example; break;
      case '#welcome': default_message = welcome_notes; break;
      case '#test-all': default_message = test_all; break;
      default: break;
    }

  const [displayGlobalLog, setDisplayGlobalLog] = useState(false)
  const [content, setContent] = useState(default_message)

  const entireFileIsComments = content.split(/\r?\n/).every((line) => {
    const isCommentLine = line.match(/^\s*#/)
    const isBlankLine = line.match(/^\s*$/)
    return isCommentLine || isBlankLine
  })

  const editorRef = useRef(null);
  useEditable(editorRef, setContent)

  let tokens = null
  let ast = null
  let output = null
  let error = null
  global.my_log = ""
  try {
    window.postALog(`LEXING`, "")
    const { tokens: t, errorMessage } = lex(content)
    if (errorMessage) { throw new FC_Error(`lex error: ${errorMessage}`) }
    tokens = t
    window.postALog(`PARSING`, "")
    ast = parse(tokens)
    window.postALog(`INTERPRETING`, "")
    output = printOutput(interpret(ast))
  } catch (e) {
    error = e
  }

  // window.my_log += `\n--------------------\n${JSON.stringify(ast)}`

  return (
    <>
      <style>{`
    :root {
      --source-background: #fff;
      --background: #eee;
      --foreground: #000;
      --border: #000;
      --comment: #999;
      --hover: #eee;
      --active: #ccc;
      --border-width: 1px;
      --alert: #cee;
    }

    @media (prefers-color-scheme: dark) {
      :root {
        --source-background: #000;
        --background: #111;
        --foreground: #eee;
        --border: #ccc;
        --comment: #aaa;
        --hover: #555;
        --active: #333;
        --border-width: 0.5px;
        --alert: #ece;
      }
    }
    body {
      background-color: var(--background);
      color: var(--foreground);
    }

    details > summary:hover { cursor: pointer }
    .my-button { background-color: var(--background); }
    .my-button:not(.active):hover { cursor: pointer; background-color: var(--hover); }
    .my-button.active { background-color: var(--active); }
    `}</style>
      <div style={{
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'column',
        height: '100vh',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'bottom',
          flexDirection: 'row',
          alignContent: "stretch",
          padding: "0 2em",
        }}>
          <div style={{ display: 'flex', gap: '10px', height: "calc(100% - 1rem)", margin: '10px 0', width: '100%' }}>
            <ExampleButton sourceContent={blank_example} label="Blank" />
            <ExampleButton sourceContent={storage_example} label="Storage Example" />
            <ExampleButton sourceContent={invocation_example} label="Invocation Example" />
            <ExampleButton sourceContent={function_example} label="Function Example" />
            <ExampleButton sourceContent={math_example} label="Math Example" />
            <ExampleButton sourceContent={list_example} label="List Example" />
            <ExampleButton sourceContent={full_example} label="Full Example" />
            <ExampleButton sourceContent={welcome_notes} label="Welcome Notes" />
            {/* <NotifyBadge label={`entireFileIsComments: ${entireFileIsComments}`} /> */}
            <div style={{flexGrow: 1}}></div>
            <FreeCellButton onClick={()=>{setDisplayGlobalLog(!displayGlobalLog)}}>Global Log: {displayGlobalLog ? 'visible' : 'hidden'}</FreeCellButton>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignContent: "stretch",
          flexDirection: 'row',
          gap: '10px',
          padding: '0 2em 1em',
          minHeight: '400px',
        }}>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '16px',
              border: 'var(--border-width) solid var(--border)',
              outline: 'none',
              margin: 0,
              width: '100%',
              padding: '0.5em',
              backgroundColor: 'var(--source-background)',
            }}
            ref={editorRef}
          >
            {
              content.split(/\r?\n/).map((line, i, arr) => (
                <React.Fragment key={i}>
                  {
                    (!entireFileIsComments
                      && (
                        <span style={{
                          color: line.match(/^\s*#/) ? "var(--comment)" : "var(--foreground)",
                        }}>
                          {line}
                        </span>
                      ))
                    || (() => {
                      const [_full, comment, rest] = line.match(/(^[\s#]*)(.*)/)

                      return (
                        <span>
                          <span style={{ color: "var(--comment)" }}>{comment}</span>
                          <span style={{ color: "var(--foreground)" }}>{rest}</span>
                        </span>
                      )
                    })()
                  }

                  {i < arr.length - 1 ? '\n' : null}
                </React.Fragment>
              ))
            }
          </pre>

          {!error && (
            <pre style={{ margin: 0, padding: '0.5em', fontSize: '16px', width: '100%', border: 'var(--border-width) solid var(--border)' }}>{output}</pre>
          )}

          {error && (
            <pre style={{ margin: 0, padding: '0.5em', fontSize: '16px', width: '100%', border: 'var(--border-width) solid red' }}>
              {forceString(error.message)}
              
              {tokens && !output && (
              <table style={{margin: '1em', border: '1px solid var(--border)', borderCollapse: 'collapse'}}>
              <tbody>{tokens.map((token, i) => (
                <tr key={i}>
                  <td style={{padding: '5px 10px', border: '1px solid var(--border)', textAlign: 'right'}}>{i}</td>
                  <td style={{padding: '5px 10px', border: '1px solid var(--border)', textAlign: 'left'}}>{forceString(token.type)}</td>
                  <td style={{padding: '5px 10px', border: '1px solid var(--border)', textAlign: 'right'}}>{token.body ? `"${token.body}"` : null}
                  {token.numerator ? ` ${token.numerator}/${token.denominator}` : null}
                  </td>
                </tr>
              ))}
              </tbody>
              </table>
              )}
            </pre>
          )}

          {displayGlobalLog && (global.my_log.length > 0 && (
            <pre style={{ margin: 0, padding: '0.5em', fontSize: '16px', width: '100%', border: 'var(--border-width) solid dodgerblue' }}>{global.my_log}</pre>
          ))}
        </div>
      </div>
    </>
  );
}

export default App;
